// src/pages/admin/tabs/awards/AwardsListPage.jsx
import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import Admin, { AdminAPI as NamedAdminAPI } from '../../../../api/admin';

const API = NamedAdminAPI ?? Admin?.AdminAPI;

const Wrap = styled.div`display:grid; gap:20px;`;
const Card = styled.div`
  border:1px solid var(--bdr); background:var(--card); border-radius:14px; padding:16px; box-shadow:var(--shadow);
`;
const Actions = styled.div`display:flex; gap:10px; flex-wrap:wrap; align-items:center;`;
const Btn = styled(Link)`
  padding:10px 14px; border-radius:12px; border:1px solid var(--bdr);
  background:var(--card); font-weight:700; text-decoration:none; cursor:pointer;
  &[data-primary="true"]{ background:var(--primary); color:var(--primaryFg); border-color:transparent; }
`;
const GhostBtn = styled.button`
  padding:8px 10px; border-radius:10px; border:1px solid var(--bdr); background:var(--card); font-weight:700; cursor:pointer;
`;
const Table = styled.table`
  width:100%; border-collapse:collapse;
  th, td{ border-bottom:1px solid var(--bdr); padding:10px 8px; text-align:left; }
  th{ font-weight:700; }
  img{ height:44px; width:72px; object-fit:cover; border-radius:6px; }
`;
const Filters = styled.div`display:flex; gap:8px; flex-wrap:wrap; align-items:center;`;
const Drag = styled.span`cursor:grab; opacity:.7; user-select:none;`;

function Toggle({ value, onChange, trueLabel='Да', falseLabel='Нет' }){
  const v = !!value;
  return (
    <div style={{display:'inline-flex', border:'1px solid var(--bdr)', borderRadius:10, overflow:'hidden'}}>
      <button type="button" onClick={()=>onChange(true)}  style={{padding:'6px 10px', fontWeight:700, border:'none', background: v ? 'var(--primary)' : 'var(--card)', color: v ? 'var(--primaryFg)' : 'inherit'}}>{trueLabel}</button>
      <button type="button" onClick={()=>onChange(false)} style={{padding:'6px 10px', fontWeight:700, border:'none', background: !v ? 'var(--primary)' : 'var(--card)', color: !v ? 'var(--primaryFg)' : 'inherit'}}>{falseLabel}</button>
    </div>
  );
}

export default function AwardsListPage(){
  const nav = useNavigate();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // filters
  const [q, setQ] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [featuredOnly, setFeaturedOnly] = React.useState(false);
  const [isActive, setIsActive] = React.useState('');
  const [yf, setYF] = React.useState('');
  const [yt, setYT] = React.useState('');

  const load = React.useCallback(async ()=>{
    setLoading(true);
    try{
      if (!API?.listAwards) throw new Error('AdminAPI.listAwards is undefined (проверь экспорт/импорт)');
      const params = {
        q: q || undefined,
        category: category || undefined,
        featured_only: featuredOnly || undefined,
        is_active: (isActive === '' ? undefined : (isActive ? 'true' : 'false')),
        year_from: yf || undefined,
        year_to: yt || undefined,
        page_size: 100
      };
      const j = await API.listAwards(params);
      setItems(j.awards || j.items || []);
    }catch(err){
      console.error(err);
      alert('Не удалось загрузить награды');
    }finally{ setLoading(false); }
  }, [q, category, featuredOnly, isActive, yf, yt]);

  React.useEffect(()=>{ load(); }, [load]);

  const del = async (id)=>{
    if(!window.confirm('Удалить награду?')) return;
    try{
      await API.deleteAward(id);
      await load();
    }catch(err){
      console.error(err);
      alert(err?.payload?.details || err?.message || 'Ошибка удаления');
    }
  };

  // DnD reorder (в рамках текущего списка)
  const dragFrom = React.useRef(null);
  const onDragStart = (idx)=> (e)=> { dragFrom.current = idx; e.dataTransfer.effectAllowed='move'; };
  const onDragOver  = (idx)=> (e)=> { e.preventDefault(); e.dataTransfer.dropEffect='move'; };
  const onDrop = (idx)=> async (e)=>{
    e.preventDefault();
    const from = dragFrom.current;
    if (from===null || from===undefined || from===idx) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    const payload = next.map((it, i)=> ({ id: it.id, sort_order: i }));
    setItems(next);
    dragFrom.current = null;
    try{
      await API.reorderAwards(payload);
    }catch(err){
      console.error(err);
      alert('Не удалось сохранить порядок');
      load();
    }
  };

  return (
    <Wrap>
      <Card>
        <Actions>
          <h3 style={{margin:0}}>Награды</h3>
          <div style={{flex:1}} />
          <Btn to="/admin/awards/new" data-primary="true">+ Создать</Btn>
        </Actions>
      </Card>

      <Card>
        <Filters>
          <input placeholder="Поиск…" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>{ if(e.key==='Enter') load(); }} />
          <input placeholder="Категория (напр. award)" value={category} onChange={(e)=>setCategory(e.target.value)} />
          <span>Только избранные:</span>
          <Toggle value={featuredOnly} onChange={setFeaturedOnly} />
          <span>Активна:</span>
          <select value={String(isActive)} onChange={e=>setIsActive(e.target.value==='true' ? true : e.target.value==='false' ? false : '')}>
            <option value="">Все</option>
            <option value="true">Да</option>
            <option value="false">Нет</option>
          </select>
          <input style={{width:110}} type="number" placeholder="Год от" value={yf} onChange={e=>setYF(e.target.value)} />
          <input style={{width:110}} type="number" placeholder="Год до" value={yt} onChange={e=>setYT(e.target.value)} />
          <GhostBtn onClick={load} disabled={loading}>{loading ? 'Загрузка…' : 'Применить'}</GhostBtn>
        </Filters>
      </Card>

      <Card>
        <Table>
          <thead>
            <tr>
              <th style={{width:28}}></th>
              <th>ID</th>
              <th>Изображение</th>
              <th>Заголовок</th>
              <th>Категория</th>
              <th>Год</th>
              <th>Избранная</th>
              <th>Активна</th>
              <th>Сорт.</th>
              <th style={{width:220}}></th>
            </tr>
          </thead>
          <tbody>
            {(items||[]).map((it, idx)=>(
              <tr key={it.id}
                  draggable
                  onDragStart={onDragStart(idx)}
                  onDragOver={onDragOver(idx)}
                  onDrop={onDrop(idx)}
              >
                <td><Drag title="Перетащите для сортировки">↕</Drag></td>
                <td>{it.id}</td>
                <td>{(it.image_thumb || it.image) ? <img src={it.image_thumb || it.image} alt="" /> : null}</td>
                <td>{it.title_ru || it.title_en || it.title}</td>
                <td>{it.category || '—'}</td>
                <td>{it.year ?? '—'}</td>
                <td>{it.is_featured ? 'Да' : 'Нет'}</td>
                <td>{it.is_active ? 'Да' : 'Нет'}</td>
                <td>{it.sort_order ?? 0}</td>
                <td style={{display:'flex', gap:8}}>
                  <Btn to={`/admin/awards/${it.id}`}>Редактировать</Btn>
                  <GhostBtn onClick={()=>del(it.id)}>Удалить</GhostBtn>
                </td>
              </tr>
            ))}
            {!items.length && !loading && (
              <tr><td colSpan={10} style={{opacity:.7}}>Нет данных</td></tr>
            )}
          </tbody>
        </Table>
      </Card>
    </Wrap>
  );
}
